/**
 * Data Last Renewed: 08/01/2023
 *
 * Using an object to store backup NLPSettings.
 * Don't use JSON file to store data, becaue it's required to use 'fetch()' to read on browser.
 * And fetch is an async function. Calling async function in React can complicate things,
 * especially in the nlpSettings.js script. Besides, using an object for storing, we can
 * achieve the same result.
 *
 * [Note]
 * I disabled eslint rules for this entire file because I want us easily insert raw data here.
 */
const backupNLPSetting = {
  id: 1,
  travelAgency: 'TravelWits',
  travelAgencyId: 1,
  nlpInstructions: {
    'Interface - Cruise Instruction': {
      id: 1,
      name: 'Interface - Cruise Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 1,
          name: 'Do Not Recommend Competitors',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'Refrain from recommending other travel agencies or travel websites beyond the one you are currently assisting with.',
        },
        {
          id: 2,
          name: 'Limit Response Tokens',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Limit responses to {{NLP_RESPONSE_TOKEN_LIMIT}} tokens.',
        },
        {
          id: 3,
          name: 'Do Not Make Up URL Links',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Do not make up URL links.',
        },
        {
          id: 4,
          name: 'Avoid Mentioning Data Source',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid mentioning how data is provided.',
        },
        {
          id: 5,
          name: 'Avoid Duplication in Response',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid including duplicated information in one response.',
        },
        {
          id: 22,
          name: 'Task For Model',
          forFeature: 'Interface Cruise',
          type: 'INTRODUCTION',
          value:
            "You are a virtual travel assistant. Your task is to answer users' questions about a cruise itinerary using provided data about the cruise and cabin availabilities for each selected stateroom. The data will be enclosed within a specified delimiter {{DELIMITER}}.",
        },
        {
          id: 6,
          name: 'Analyze Data Carefully',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            "Analyze the data with accuracy! Before providing an answer, you must go over all the cabins' data to understand the cabin full name (e.g., Inside: IX - BEST_FARE), total price, is deposit refundable or not, and other features.",
        },
        {
          id: 7,
          name: 'Only Consider Explicit Features',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            "When filtering and comparing cabins, only consider the explicit features mentioned in the data. If a cabin with such a feature doesn't exist, inform the user politely. For example, if you can't find a cabin with <feature_X>, you can say \"I'm sorry, but I can't find a cabin with <feature_X>\".",
        },
        {
          id: 8,
          name: 'Pay Attention To Special Deal',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            "Information about cabin's availability for extra credits, special deals and promotions are embedded within cabin's full name (e.g., Oceanview: OA - Internet Package and $50 Shore Ex credit). If users asks for such information, you must look carefully into cabins' full name.",
        },
        {
          id: 9,
          name: 'Pay Attention To Refundable',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            "Be aware that if the phrase 'NON-REFUNDABLE' or 'nonRefundable' is found in the cabin data, it indicates that the deposit for that cabin cannot be refunded.",
        },
        {
          id: 10,
          name: 'Response With Price Info',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            'The price for cabin is \'total price before taxes and fees\'. When answer questions about cabin\'s pricing, you must clarify that the price is "before taxes and fees". For example, "The total price for cabin X is $1000 before taxes and fees." It\'s crucial to be clear on this!',
        },
        {
          id: 11,
          name: 'When Stateroom Not Specified',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            'If the user does not specify a stateroom, find the best cabin match in each stateroom and format your response as: "For <stateroom1>, <the_result_you_found_for_stateroom1>. For <stateroom2>, <the_result_you_found_for_stateroom2>.',
        },
        {
          id: 12,
          name: 'When Stateroom Specified',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value: 'If a stateroom is specified, find the best cabin match within that stateroom.',
        },
        {
          id: 13,
          name: 'Response Cabin Queries',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            'When answering questions about cabins, always include cabin category and rate option, the stateroom number, and the value of the feature in question in your response. For example, "In <stateroom1>, the <feature_X> cabin with <feature_Y> is <cabin_category_and_rate_option>, the total price before taxes and fees is <total_price>.',
        },
        {
          id: 14,
          name: 'Steps To Filter Cabins',
          forFeature: 'Interface Cruise',
          type: 'ANALYSIS_GUIDE',
          value:
            "When selecting cabins based on specific criteria (for example, 'the cheapest cabin that has refundable deposit and balcony'), follow these steps: 1. Filter the cabins that meet the criteria. In this example, filter for cabins that have 'refundable' keyword and 'Balcony' keyword 2. From the filtered list, find the cabin with the lowest 'Total Price'. If there are multiple cabins with the same category that meet the criteria, you can choose any of them or the one with the lowest price if price is a factor.",
        },
        {
          id: 15,
          name: 'Do Not Mix Use Data From Different Staterooms',
          forFeature: 'Interface Cruise',
          type: 'DATA_EXPLANATION',
          value:
            "In the data zone below, you will find multiple sets of staterooms data. Sometimes there's only 1 stateroom. In case of multiple staterooms data, remember that data in each set pertains to that stateroom. For example data in 'Set 1' is only related to 'Stateroom 1'. Do not mix use the data from different sets.",
        },
        {
          id: 16,
          name: 'Structure of Stateroom Dataset',
          forFeature: 'Interface Cruise',
          type: 'DATA_EXPLANATION',
          value:
            "Explanation of the staterooms dataset: Each set of data contains a list of cabins. Each line stands for one cabin. Information about this cabin is separated by period symbol. Lines under one set of data is sorted in ascending order based on the 'total price' (e.g., $1000). That is, the line with the lowest total price is listed first and the line with the highest total price is listed last.",
        },
        {
          id: 48,
          name: 'Format To List For Similar Items',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'When listing similar items, such as restaurants, format them on separate lines for readability.',
        },
        {
          id: 51,
          name: 'Do Not Limit Yourself To The Provided Data',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'In your responses, leverage your existing knowledge, not just the given data. For instance, if asked about nearby fine dining options, suggest restaurants based on your knowledge, even without explicit data. Your goal is to assist the user by providing the best possible answer or guiding them to the information they seek.',
        },
      ],
      mapForPlacingRules:
        'Introductions:\n{{INTRODUCTION}}\n\nCore rules you must follow:\n{{SHARED_RULE}}\n\nFollow these rules when analyzing provided data:\n{{ANALYSIS_GUIDE}}\n\nHere is an explanation of the data:\n{{DATA_EXPLANATION}}\n\n{{DELIMITER}}\n\n{{DETAILED_DATA}}\n\n{{DELIMITER}}',
      note: "This instruction will be generated after users click the NLPInterface's input box on cruise webpage. It will provide NLP model the knowledge about the cruise, and teach it how to read the data.",
    },
    'Interface - Cruise Before Data Instruction': {
      id: 2,
      name: 'Interface - Cruise Before Data Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 17,
          name: 'Task For Model With High Level Data',
          forFeature: 'Interface Cruise',
          type: 'INTRODUCTION_HIGH_DATA',
          value:
            "However, you are currently provided with very high-level data about this cruise itinerary. Remember, when answering questions, still let the users know that you are still fetching detailed data, so you can't answer specific questions yet. The high-level data is enclosed within a delimiter {{DELIMITER}}.",
        },
      ],
      mapForPlacingRules:
        '{{{GENERAL_BEFORE_DATA_INSTRUCTION}}}\n\nIntroduction about high-level data:\n{{INTRODUCTION_HIGH_DATA}}\n\n{{DELIMITER}}\n\n{{HIGH_LEVEL_DATA}}\n\n{{DELIMITER}}',
      note: 'This instruction is generated even before user click on NLPInterface\'s input box. The instruction carries high-level data about the cruise. We use it to enable NLP model to answer general questions before detailed data kicks in.\n\n{{{GENERAL_BEFORE_DATA_INSTRUCTION}}} stands for the instruction "Interface - General Before Data Intro" instead of a rule.',
    },
    'Interface - Hotel Instruction': {
      id: 3,
      name: 'Interface - Hotel Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 1,
          name: 'Do Not Recommend Competitors',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'Refrain from recommending other travel agencies or travel websites beyond the one you are currently assisting with.',
        },
        {
          id: 2,
          name: 'Limit Response Tokens',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Limit responses to {{NLP_RESPONSE_TOKEN_LIMIT}} tokens.',
        },
        {
          id: 3,
          name: 'Do Not Make Up URL Links',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Do not make up URL links.',
        },
        {
          id: 4,
          name: 'Avoid Mentioning Data Source',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid mentioning how data is provided.',
        },
        {
          id: 5,
          name: 'Avoid Duplication in Response',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid including duplicated information in one response.',
        },
        {
          id: 27,
          name: 'Task For Model',
          forFeature: 'Interface Hotel',
          type: 'INTRODUCTION',
          value:
            "You are a virtual travel assistant. You will answer users' questions about a hotel and also things to do around it. You will be provided with data about the hotel. The data will be enclosed within a specified delimiter {{DELIMITER}}.",
        },
        {
          id: 41,
          name: 'Never Making Up Room Data',
          forFeature: 'Interface Hotel',
          type: 'ANALYSIS_GUIDE',
          value:
            'When users inquire about hotel rooms possessing specific features, concentrate solely on the rooms that are explicitly included in the provided dataset. If no room with the specified features can be found, avoid making up any hotel room. Instead, politely communicate to the users that no rooms meeting their specified criteria are available.',
        },
        {
          id: 48,
          name: 'Format To List For Similar Items',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'When listing similar items, such as restaurants, format them on separate lines for readability.',
        },
        {
          id: 51,
          name: 'Do Not Limit Yourself To The Provided Data',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'In your responses, leverage your existing knowledge, not just the given data. For instance, if asked about nearby fine dining options, suggest restaurants based on your knowledge, even without explicit data. Your goal is to assist the user by providing the best possible answer or guiding them to the information they seek.',
        },
      ],
      mapForPlacingRules:
        'Introductions:\n{{INTRODUCTION}}\n\nCore rules you must follow:\n{{SHARED_RULE}}\n\nFollow these rules when analyzing provided data:\n{{ANALYSIS_GUIDE}}\n\n{{DELIMITER}}\n\n{{DETAILED_DATA}}\n\n{{DELIMITER}}',
      note: "This instruction will be generated after users click the NLPInterface's input box on hotel webpage. It will provide NLP model the knowledge about the hotel, and teach it how to read the data.",
    },
    'Interface - Hotel Before Data Instruction': {
      id: 4,
      name: 'Interface - Hotel Before Data Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 28,
          name: 'Task For Model With High Level Data',
          forFeature: 'Interface Hotel',
          type: 'INTRODUCTION_HIGH_DATA',
          value:
            "However, you are currently provided with very high-level data about this hotel. You will answer users' questions about a hotel and also things to do around it based on the high-level data provided. The high-level data is enclosed within a specified delimiter {{DELIMITER}} below. When answering questions, if users ask detailed question about the hotel and you can't answer, let the users know that you are still fetching detailed data.",
        },
      ],
      mapForPlacingRules:
        '{{{GENERAL_BEFORE_DATA_INSTRUCTION}}}\n\nIntroduction about high-level data:\n{{INTRODUCTION_HIGH_DATA}}\n\n{{DELIMITER}}\n\n{{HIGH_LEVEL_DATA}}\n\n{{DELIMITER}}',
      note: 'This instruction is generated even before user click on NLPInterface\'s input box. The instruction carries high-level data about the hotel. We use it to enable NLP model to answer general questions before detailed data kicks in.\n\n{{{GENERAL_BEFORE_DATA_INSTRUCTION}}} stands for the instruction "Interface - General Before Data Intro" instead of a rule.',
    },
    'Interface - Hotel No Data Instruction': {
      id: 7,
      name: 'Interface - Hotel No Data Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 1,
          name: 'Do Not Recommend Competitors',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'Refrain from recommending other travel agencies or travel websites beyond the one you are currently assisting with.',
        },
        {
          id: 2,
          name: 'Limit Response Tokens',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Limit responses to {{NLP_RESPONSE_TOKEN_LIMIT}} tokens.',
        },
        {
          id: 3,
          name: 'Do Not Make Up URL Links',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Do not make up URL links.',
        },
        {
          id: 4,
          name: 'Avoid Mentioning Data Source',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid mentioning how data is provided.',
        },
        {
          id: 5,
          name: 'Avoid Duplication in Response',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid including duplicated information in one response.',
        },
        {
          id: 29,
          name: 'Task For Model When No Detailed Data After Fetch',
          forFeature: 'Interface Hotel',
          type: 'INTRODUCTION_NO_DATA',
          value:
            "You are a virtual travel assistant. Your task is to answer user's question regarding a hotel. The system has already fetched detailed data for you, but it is empty. Currently, only high-level data is available to you and it is enclosed within a specified delimiter {{DELIMITER}} below. If you don't have data to answer users questions, you can say \"I'm sorry, but that data doesn't exist.\" or \"I'm sorry, but I don't have that information.",
        },
        {
          id: 48,
          name: 'Format To List For Similar Items',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'When listing similar items, such as restaurants, format them on separate lines for readability.',
        },
        {
          id: 51,
          name: 'Do Not Limit Yourself To The Provided Data',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'In your responses, leverage your existing knowledge, not just the given data. For instance, if asked about nearby fine dining options, suggest restaurants based on your knowledge, even without explicit data. Your goal is to assist the user by providing the best possible answer or guiding them to the information they seek.',
        },
      ],
      mapForPlacingRules:
        'Introductions:\n{{INTRODUCTION_NO_DATA}}\n\nCore rules you must follow:\n{{SHARED_RULE}}\n\n{{DELIMITER}}\n\n{{HIGH_LEVEL_DATA}}\n\n{{DELIMITER}}',
      note: "The row contains 'rules' to be used in hotel intro message when no detailed data is available after fetching. We will provide NLP model the high-level data, and make it emphasize there's no detailed data to users.",
    },
    'Interface - General Before Data Instruction': {
      id: 5,
      name: 'Interface - General Before Data Instruction',
      variant: 'Default',
      nlpRules: [
        {
          id: 1,
          name: 'Do Not Recommend Competitors',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'Refrain from recommending other travel agencies or travel websites beyond the one you are currently assisting with.',
        },
        {
          id: 2,
          name: 'Limit Response Tokens',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Limit responses to {{NLP_RESPONSE_TOKEN_LIMIT}} tokens.',
        },
        {
          id: 3,
          name: 'Do Not Make Up URL Links',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Do not make up URL links.',
        },
        {
          id: 4,
          name: 'Avoid Mentioning Data Source',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid mentioning how data is provided.',
        },
        {
          id: 5,
          name: 'Avoid Duplication in Response',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value: 'Avoid including duplicated information in one response.',
        },
        {
          id: 18,
          name: 'Task For Model',
          forFeature: 'Interface Before Data',
          type: 'INTRODUCTION',
          value:
            "You are a virtual travel assistant. Remember the following rules when responding to user's questions.",
        },
        {
          id: 19,
          name: 'Way To Response When No Specific Data Available',
          forFeature: 'Interface Before Data',
          type: 'RESPONSE_RULE',
          value:
            "You currently don't have specific data yet. When responding to every queries, please prefix your answers with the phrase like: 'I am collecting detailed data to fully address your query. However, based on my current knowledge, ' to clearly tell the users the status of data fetching.",
        },
        {
          id: 20,
          name: 'Type Of Questions To Answer',
          forFeature: 'Interface Before Data',
          type: 'RESPONSE_RULE',
          value:
            "Your task is to answer general queries related to all aspects of travel, which can include but is not limited to information on hotels, flights, cruises, local attractions, restaurants, bars, and other leisure activities. Your aim is to provide comprehensive travel advice and suggestions based on user's queries before detailed data kicks in.",
        },
        {
          id: 21,
          name: 'Response To Specific Questions',
          forFeature: 'Interface Before Data',
          type: 'RESPONSE_RULE',
          value:
            "If users ask specific questions such as 'What is the cheapest room with balcony and refundable deposit?', and you don't have data about that, politely inform them that you are still collecting the required data. You might say, 'I'm sorry, but I am still fetching the detailed data. In the meantime, I would be glad to answer any general questions you may have.' Remember, it is crucial to mention that you are still fetching the data! Otherwise, the users will get confused that why you don not have access to the data.",
        },
        {
          id: 25,
          name: 'Try Your Best',
          forFeature: 'Interface Before Data',
          type: 'RESPONSE_RULE',
          value:
            "Always maintain a friendly and helpful tone throughout the conversation. Strive to answer the user's questions to the best of your abilities.",
        },
        {
          id: 48,
          name: 'Format To List For Similar Items',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'When listing similar items, such as restaurants, format them on separate lines for readability.',
        },
        {
          id: 51,
          name: 'Do Not Limit Yourself To The Provided Data',
          forFeature: 'Shared',
          type: 'SHARED_RULE',
          value:
            'In your responses, leverage your existing knowledge, not just the given data. For instance, if asked about nearby fine dining options, suggest restaurants based on your knowledge, even without explicit data. Your goal is to assist the user by providing the best possible answer or guiding them to the information they seek.',
        },
      ],
      mapForPlacingRules:
        "Introductions:\n{{INTRODUCTION}}\n\nCore rules you must follow:\n{{SHARED_RULE}}\n\nFollow these rules when responding to user's questions:\n{{RESPONSE_RULE}}",
      note: "The row contains general 'rules' to be used before data is available, regardless what type of travel option the assistant is serving for.",
    },
  },
  nlpConstants: {
    MAX_IRRELEVANT_QUERY_COUNT: {
      id: 1,
      name: 'MAX_IRRELEVANT_QUERY_COUNT',
      variant: 'Default',
      value: '50',
      type: 'Frontend Config',
      note: 'The maximum number of irrelevant queries the user can send before we start blocking their queries.\n\nWhen the irrelevant queries user send to the NLP interface in the frontend is less than {MAX_IRRELEVANT_QUERY_COUNT}, we will still answer these queries. However, once the amount of irrelevant queries exceeded the amount specified by this constant, we will tell the user his/her query is irrelevant',
    },
    MAX_QUERY_TOKEN_COUNT: {
      id: 2,
      name: 'MAX_QUERY_TOKEN_COUNT',
      variant: 'Default',
      value: '500',
      type: 'Frontend Config',
      note: 'The maximum tokens we will allow a user to send in a single message to NLP interface. \n\nIt helps us to comply with the token limit of current NLP model - ChatGPT.\n\nIf the user\'s query contains more than this amount of tokens, we will show an error like "The query is too long" on the NLP interface. So, that query will not be processed.',
    },
    MAX_TOKEN_COUNT: {
      id: 3,
      name: 'MAX_TOKEN_COUNT',
      variant: 'Default',
      value: '3800',
      type: 'Frontend Config',
      note: 'The maximum number of tokens that we will send to ChatGPT. The official token limit is 4097 (ChatGPTTurbo 3.5) shared between prompt and response. In other words, that if we send a 4000-token prompt, ChatGPT can only respond in 97 tokens. So we should make sure to send much less than the official limit of 4097, so that ChatGPT has breathing room.',
    },
    MAX_NUM_HISTORICAL_MESSAGES: {
      id: 4,
      name: 'MAX_NUM_HISTORICAL_MESSAGES',
      variant: 'Default',
      value: '10',
      type: 'Frontend Config',
      note: "The maximum number of historical messages we will keep in the conversation history. For example, we would keep maximum 10 prior conversations, including user's query and NLP model's response.\n\nWe will send the conversation history to the NLP model like ChatGPT, so it knows what are discussed previously. So, we can provide our user a seamless conversation experience.",
    },
    MIN_NUM_HISTORICAL_MESSAGES: {
      id: 5,
      name: 'MIN_NUM_HISTORICAL_MESSAGES',
      variant: 'Default',
      value: '5',
      type: 'Frontend Config',
      note: 'The minimum number of historical messages we will keep in the conversation history. To comply with token limit, we sometimes need to remove some prior conversation. However, we want to keep at least MIN_NUM_HISTORICAL_MESSAGES amount of historical messages to ensure the NLP model has the context of what have discussed. \n\nOtherwise, the model can\'t continue the conversation. For example, if user asks "What\'s the cheapest room", the model response "The cheapest room is STANDARD with price $400". Then, the user asks "How many beds are in that room?". If the model doesn\'t have the prior conversation, it will be confused about what does "that room" refers to.',
    },
    NLP_RESPONSE_TOKEN_LIMIT: {
      id: 6,
      name: 'NLP_RESPONSE_TOKEN_LIMIT',
      variant: 'Default',
      value: '200',
      type: 'Frontend Config',
      note: "We will tell NLP model to limit its response to this many tokens. Even though in reality, it tends to surpass the limit a little bit, it's still a good idea to set a limit. It prevents the model provides too much response.",
    },
    DELIMITER: {
      id: 7,
      name: 'DELIMITER',
      variant: 'Default',
      value: '####',
      type: 'Frontend Config',
      note: 'The delimiter we use to separate the section of messages in system instruction. \n\nSystem instruction will be sent to NLP model. It usually contains rules/guidelines and data to tell model what actions to do and what information it has for doing those actions.\n\nDELIMITER helps separating the rules/guidelines and data in the system instruction, so the model can identify the information easier.',
    },
    MAX_CRUISE_STATEROOMS_ALLOWED_USING_NLP: {
      id: 8,
      name: 'MAX_CRUISE_STATEROOMS_ALLOWED_USING_NLP',
      variant: 'Default',
      value: '1',
      type: 'Frontend Config',
      note: "The maximum number of staterooms we allow for the NLP interface to appear on the cruise page.\n\nWe are having this constant because there's a token limit on query to NLP model. Multiple staterooms means query with longer tokens. Currently, the token limit can't accomodate data of two staterooms. That's why this constant is set to 1 by default.",
    },
    WAIT_NLP_RESPONSE_TIMEOUT_LIMIT: {
      id: 9,
      name: 'WAIT_NLP_RESPONSE_TIMEOUT_LIMIT',
      variant: 'Default',
      value: '15',
      type: 'Frontend Config',
      note: 'The maximum number of seconds to wait for a response from the NLP model. If the response time exceeds the limit, we will show show the user "It took too long for ChatGPT to response" on NLP interface.',
    },
    SHOULD_STREAM_CHATGPT_RESPONSE: {
      id: 10,
      name: 'SHOULD_STREAM_CHATGPT_RESPONSE',
      variant: 'Default',
      value: 'true',
      type: 'Frontend Config',
      note: 'Set this option "true" if we should display NLP model\'s response chunk by chunk as we receive it. Set this option "false" if we should simply receive entire response all at once. \n\nUsing Streaming can also help with preventing exceeding NLP_RESPONSE_TIMEOUT_LIMIT, because we will receive message token-by-token and it\'s faster than receiving a bulk message.',
    },
    HOTEL_EXTRACTOR_TIMEOUT_LIMIT: {
      id: 11,
      name: 'HOTEL_EXTRACTOR_TIMEOUT_LIMIT',
      variant: 'Default',
      value: '0',
      type: 'Frontend Config',
      note: "The maximum number of seconds to wait for each request when getting hotel data. Currently, the default is 0, which means no timeout.\n\nIt's used in HotelExtractor.cs script in the frontend. Reading that script for details.",
    },
    CRUISE_EXTRACTOR_TIMEOUT_LIMIT: {
      id: 12,
      name: 'CRUISE_EXTRACTOR_TIMEOUT_LIMIT',
      variant: 'Default',
      value: '0',
      type: 'Frontend Config',
      note: "The maximum number of seconds to wait for each request when getting cruise data. Currently, the default is 0, which means no timeout.\n\nIt's used in CruiseExtractor.cs script in the frontend. Reading that script for details.",
    },
    PREFER_MODEL: {
      id: 14,
      name: 'PREFER_MODEL',
      variant: 'Default',
      value: 'GPT4',
      type: 'Frontend Config',
      note: 'The name of preferred NLP model to use. It will be applied to the frontend.\n\nLeave the "Value" column blank indicates using backend\'s default model. \n\nBy 7/26/2023, the default PREFER_MODEL in backend is GPT4.',
    },
    ALT_MODEL: {
      id: 15,
      name: 'ALT_MODEL',
      variant: 'Default',
      value: 'ChatGPTTurbo',
      type: 'Frontend Config',
      note: 'The name of alternative model for the NLP endpoint, in case the call to preferred model failed.\n\nLeave the "Value" column blank indicates using backend\'s default model. Check backend\'s default model in NLPServices script.\n\nLeave the "Value" column blank indicates using backend\'s default model. \n\nBy 7/26/2023, the default ALT_MODEL in backend is ChatGPTTurbo.',
    },
    DATA_LOADING_STATUS: {
      id: 16,
      name: 'DATA_LOADING_STATUS',
      variant: 'Default',
      value:
        '{    "SUCCESS":"Data Ready",    "FAILED":"Load Failure",    "IN_PROCESS":"Loading Data" }',
      type: 'Frontend Enum',
      note: 'The text shown besides the NLP interface which represents the data fetching status.',
    },
    INTERFACE_PLACEHOLDER_TEXT: {
      id: 17,
      name: 'INTERFACE_PLACEHOLDER_TEXT',
      variant: 'Default',
      value:
        '{   "DEFAULT": "What are the available amenities?",   "HOTEL": "Does this hotel have a pool?","BROCHURE":"Add fine dining options within walking distance","CRUISE": "What kind of entertainment is offered on this cruise?","SearchResults":"Ask about your hotel search results" }',
      type: 'Frontend Enum',
      note: 'The placeholder text for the input box of NLP interface.',
    },
    HOTEL_RELEVANT_TERMS: {
      id: 20,
      name: 'HOTEL_RELEVANT_TERMS',
      variant: 'Default',
      value:
        'city, fun, entertainment, flight, plane, amenit, room, pool, swim, lobby, wifi, wi-fi, wi fi, internet, cost, money, dollar, currency, bed, bathroom, toilet, shower, bath, tub, roof, deck, restaurant, dinner, lunch, breakfast, food, cafe, car, rental, beach, activit, golf, tennis, sport, gym, recreation, rating, star, bar, night, pet, smoking, lounge, parking, wheelchair, accessib, business, country, address, clean, discount, buffet',
      type: 'Frontend Relevant Terms',
      note: "The terms to help judging if a user's query is related to hotel. These words will be used to judge if the query is relevant. If not, we will still answer the query until the user hits the quota: MAX_IRRELEVANT_QUERY_COUNT, we will stop answering.\ne.g., 50 queries",
    },
    CRUISE_RELEVANT_TERMS: {
      id: 21,
      name: 'CRUISE_RELEVANT_TERMS',
      variant: 'Default',
      value:
        'cruise, city, fun, entertainment, flight, plane, amenit, room, pool, swim, wifi, wi-fi, wi fi, internet, cost, money, dollar, currency, bed, bathroom, toilet, shower, bath, tub, roof, deck, restaurant, dinner, lunch, breakfast, food, cafe, car, rental, beach, activit, golf, tennis, sport, gym, recreation, rating, star, bar, night, pet, smoking, lounge, parking, wheelchair, accessib, business, country, address, clean, discount, ship, port, excursion, ocean, sea, maritime, voyage, sail, onboard, cabin, suite, stateroom, galley, shore, dock, embark, disembark, itinerar, buffet, cruise line, nautical, marina, stopover, day at sea, port of call',
      type: 'Frontend Relevant Terms',
      note: "The terms to help judging if a user's query is related to cruise. The purpose of it is same as HOTEL_RELEVANT_TERMS.",
    },
    FROM_EMAIL_ADDRESS: {
      id: 30,
      name: 'FROM_EMAIL_ADDRESS',
      variant: 'Default',
      value: 'trips@travelwits.com',
      type: 'Inquiries Service Config',
      note: 'The email address from which to send responses to inquiries.',
    },
    EMAIL_DISPLAY_NAME: {
      id: 31,
      name: 'EMAIL_DISPLAY_NAME',
      variant: 'Default',
      value: 'TravelWits',
      type: 'Inquiries Service Config',
      note: 'The display name to use when sending responses to customer inquiries. This is the name that will be in the email signature and in the email header next to the address.',
    },
    DESIRED_HOTEL_COUNT: {
      id: 32,
      name: 'DESIRED_HOTEL_COUNT',
      variant: 'Default',
      value: '10',
      type: 'Inquiries Service Config',
      note: "The number of hotels that we will try to find whenever a user asks us to perform a search. Once we've found this number of suitable hotels, we will stop the search and send the brochure.",
    },
    MAX_HOTEL_SEARCH_COUNT: {
      id: 33,
      name: 'MAX_HOTEL_SEARCH_COUNT',
      variant: 'Default',
      value: '100',
      type: 'Inquiries Service Config',
      note: 'The maximum number of hotels we will permit the hotel search algorithm to search for.',
    },
    MAX_MAYBE_RECOMMENDATIONS: {
      id: 34,
      name: 'MAX_MAYBE_RECOMMENDATIONS',
      variant: 'Default',
      value: '3',
      type: 'Inquiries Service Config',
      note: 'The maximum number of "maybe" hotels that we will recommend to the customer. A "maybe" hotel is a hotel that ChatGPT thinks doesn\'t match the customer\'s preferences but is still worth recommending. For instance, if the customer wants a 5-star hotel with a pool, ChatGPT might decide that a 4.5-star hotel with a pool is worth mentioning.',
    },
    INQUIRIES_GPT_MODEL: {
      id: 35,
      name: 'INQUIRIES_GPT_MODEL',
      variant: 'Default',
      value: 'gpt-3.5-turbo-0613',
      type: 'Inquiries Service Config',
      note: 'The GPT model to use when parsing customer inquiries. Consult this page for a list of all models: https://platform.openai.com/docs/models/overview.\n\nOur recommended models are "gpt-3.5-turbo-0613" (fast but inaccurate) and "gpt-4" (slow and expensive but much more accurate).',
    },
  },
};

export default backupNLPSetting;
